import { FC, useState } from 'react';
import { ConnectDragPreview, ConnectDragSource, DragSourceMonitor, useDrag, useDrop } from 'react-dnd';
import { Box, Flex } from '@chakra-ui/react';
import DndSpacer from 'components/DndSpacer';

interface DragItem {
  index: number;
  type: string;
}

interface ContainerWrapperProps {
  index: number;
  isLoading: boolean;
  multipleContainers: boolean;
  removeContainer: (index: number) => void;
  dragRef: ConnectDragSource;
  previewRef: ConnectDragPreview;
  isOpen: boolean;
  toggleSectionHandler: () => void;
  isDashboard: boolean;
}

interface DndWrapperProps {
  id: string | number;
  index: number;
  isLoading: boolean;
  multipleContainers: boolean;
  reorderContainers: (dragIndex: number, hoverIndex: number) => void;
  removeContainer: (index: number) => void;
  ContainerWrapper: FC<ContainerWrapperProps>;
  isDashboard?: boolean;
}

function DndWrapper({
  id,
  index,
  reorderContainers,
  isLoading,
  multipleContainers,
  removeContainer,
  ContainerWrapper,
  isDashboard = false,
}: DndWrapperProps) {
  const [isOpen, toggleSection] = useState(true);

  const [{ targetOffset, isOver }, dropRef] = useDrop<
    DragItem,
    void,
    { targetOffset: number | undefined; isOver: boolean }
  >({
    accept: 'container',
    collect(monitor) {
      return {
        isOver: monitor.isOver(),
        targetOffset: monitor.getDifferenceFromInitialOffset()?.y,
      };
    },
    drop(item: DragItem) {
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }

      reorderContainers(dragIndex, hoverIndex);
    },
  });

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: 'container',
    item: () => {
      return { index };
    },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const showDrop = isOver && !isDragging && targetOffset;

  return (
    <Box ref={dropRef} key={`draggable-container${id}`}>
      <Flex h="8px" paddingInline="7px" align="center">
        {showDrop && targetOffset < 0 && <DndSpacer />}
      </Flex>
      <ContainerWrapper
        index={index}
        isLoading={isLoading}
        multipleContainers={multipleContainers}
        removeContainer={removeContainer}
        dragRef={dragRef}
        previewRef={previewRef}
        isOpen={isOpen}
        toggleSectionHandler={() => toggleSection(!isOpen)}
        isDashboard={isDashboard}
      />
      <Flex h="8px" paddingInline="7px" align="center">
        {showDrop && targetOffset > 0 && <DndSpacer />}
      </Flex>
    </Box>
  );
}

DndWrapper.defaultProps = {
  isDashboard: false,
};

export default DndWrapper;
